import { Box, Button, HStack, SimpleGrid, Text, useColorModeValue } from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { getSlaStatus } from './slaUtils'

export type GlanceTile = {
  id: string
  label: string
  value: string
  subValue?: string
  tone?: 'default' | 'green' | 'orange' | 'red' | 'blue'
  onClick?: () => void
  alertDot?: boolean
}

type Props = {
  tiles: GlanceTile[]
}

const toneValueColor = {
  default: 'gray.800',
  green: 'green.700',
  orange: 'orange.700',
  red: 'red.600',
  blue: 'blue.700',
}

export function slaTileFromDueAt(slaDueAt: string | null | undefined): Pick<GlanceTile, 'value' | 'subValue' | 'tone'> {
  const sla = getSlaStatus(slaDueAt)
  const tone =
    sla.urgency === 'overdue' ? 'red' : sla.urgency === 'due_soon' ? 'orange' : sla.urgency === 'on_track' ? 'green' : 'default'
  return {
    value: sla.label,
    subValue: sla.detail,
    tone,
  }
}

export default function AtAGlanceStrip({ tiles }: Props) {
  const colors = useThemeColors()
  const tileBg = useColorModeValue('#F5F4F2', 'gray.800')

  return (
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2.5} mb={3.5}>
      {tiles.map((tile) => {
        const valueColor = toneValueColor[tile.tone ?? 'default']
        const inner = (
          <>
            <HStack spacing={1.5} mb={0.5}>
              <Text fontSize="11px" color={colors.textSecondary} lineHeight="short">
                {tile.label}
              </Text>
              {tile.alertDot ? (
                <Box w="6px" h="6px" borderRadius="full" bg="red.500" flexShrink={0} aria-hidden />
              ) : null}
            </HStack>
            <Text fontSize="sm" fontWeight="500" color={valueColor} noOfLines={2} lineHeight="short">
              {tile.value}
            </Text>
            {tile.subValue ? (
              <Text fontSize="2xs" color={colors.textMuted} mt={0.5} noOfLines={1}>
                {tile.subValue}
              </Text>
            ) : null}
          </>
        )
        const tileStyles = {
          textAlign: 'left' as const,
          px: 3,
          py: 2.5,
          borderRadius: 'md',
          bg: tileBg,
          h: 'auto',
          w: '100%',
          whiteSpace: 'normal' as const,
        }
        if (tile.onClick) {
          return (
            <Button
              key={tile.id}
              variant="unstyled"
              onClick={tile.onClick}
              transition="background 0.15s"
              _hover={{ bg: colors.sidebarHoverBg }}
              {...tileStyles}
            >
              {inner}
            </Button>
          )
        }
        return (
          <Box key={tile.id} {...tileStyles}>
            {inner}
          </Box>
        )
      })}
    </SimpleGrid>
  )
}
